import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext } from 'src/shared/util/secondary-page-context';

import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { TaxesService } from 'src/shared/services/taxes.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface RegisterTaxParams {
  taxId: number;
  taxName?: string;
}

@Component({
  templateUrl: 'register-property-tax.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RegisterPropertyTaxPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly taxesService = inject(TaxesService);

  readonly form = this.fb.nonNullable.group({
    license_number: ['', Validators.required],
  });

  isRightSideContent = true;
  submitted = false;
  saving = false;
  errorMessage = '';
  taxId: number;
  taxName = '';

  readonly boundSave = () => this.save();

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<RegisterTaxParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    this.taxId = this.navCtrl.getParam('taxId') ?? params.taxId;
    this.taxName = this.navCtrl.getParam('taxName') ?? params.taxName ?? '';
    this.rightSidePanelService.setDialogPageTitle('Mark Registered');
    this.cdr.markForCheck();
  }

  async save(): Promise<void> {
    this.submitted = true;
    this.errorMessage = '';
    if (this.saving || !this.form.valid) {
      this.cdr.markForCheck();
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.saving = true;
    this.cdr.markForCheck();
    try {
      await this.taxesService.updateTax(this.taxId, {
        status: 'registered',
        license_number: this.form.getRawValue().license_number.trim(),
      });
      await this.storage.save('didMakePropertyTaxChanges', true);
      this.util.showSuccess('Tax marked registered.');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to register the tax.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
